import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  CellStyle,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';

import {
  useAddRecipToQueueMutation,
  useClearRecipQueueMutation,
  useGetRecipsInQueueQuery,
  useGetRecipsQuery,
  useRemoveRecipFromQueueMutation,
} from '@/app/services/inClinic';
import { DataGrid } from '@/components/common';
import { AnimalLink } from '@/components/common/links';
import { WithLocalStorage } from '@/components/hoc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useContextMenu } from '@/utils/context-menu';

import { useImplantSession } from '../../hooks/useImplantSession';
import type { Recipient, RecipQueueItem } from '../../types';
import { HeatNotUsedDialog } from '../dialogs/HeatNotUsedDialog';
import { RecipHistoryDialog } from '../dialogs/RecipHistoryDialog';
import {
  getAddDisabledTitle,
  getQueueDisplayName,
  getRecipInputPlaceholder,
} from './clientRecipMode';
import type { KickOutTarget } from './kickOutLogic';

const PERSISTED_GRID_CONTAINER_CLASS =
  'ag-theme-alpine ag-compact h-full overflow-hidden';
const QUEUE_GRID_WRAPPER_CLASS =
  'mt-1 h-[240px] lg:h-auto lg:min-h-0 lg:flex-1';
const IN_HEAT_GRID_WRAPPER_CLASS =
  'mt-1 h-[280px] lg:h-auto lg:min-h-0 lg:flex-1';

function matchesRecipTag(
  tag: string | null | undefined,
  search: string,
): boolean {
  const normalizedSearchTag = search.trim().toLowerCase();
  if (!normalizedSearchTag) return true;
  return String(tag ?? '')
    .toLowerCase()
    .includes(normalizedSearchTag);
}

function filterRecipsByTag<T extends { tag?: string | null }>(
  recips: T[],
  search: string,
): T[] {
  if (!search.trim()) return recips;
  return recips.filter((recip) => matchesRecipTag(recip.tag, search));
}

const recipStatusCellStyle = (params: { value?: unknown }): CellStyle => {
  const v = String(params.value ?? '').toLowerCase();
  if (v === 'open' || v === 'synched')
    return { backgroundColor: '#9fdf9f', color: '#063' };
  if (v === 'pregnant') return { backgroundColor: '#ff8080', color: '#600' };
  if (v === 'implanted') return { backgroundColor: '#ffff80', color: '#660' };
  return {};
};

interface RecipPanelProps {
  yardId: string;
  chuteNumber: number;
  clientRecipMode: boolean;
  selectedEmbryoId: string | null;
  onRecipSelect: (recipName: string, recipQueueId: number) => void;
}

export function RecipPanel({
  yardId,
  chuteNumber,
  clientRecipMode,
  selectedEmbryoId,
  onRecipSelect,
}: RecipPanelProps) {
  const { getContextMenuItems: getRecipContextMenuItems } = useContextMenu({
    idKey: 'recipId',
  });
  const [recipTag, setRecipTag] = useState('');
  const tagInputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const session = useImplantSession();
  const isEstrusSyncEnabled = useFeatureFlag('Chute_Implant_Estrus_Sync');
  const isAnimalLinkEnabled = useFeatureFlag('Implant_Link_TOG_TagName');
  const isRecipTagSearchEnabled = useFeatureFlag('Recip_Tag_Search');
  const persistGridState = useFeatureFlag('In_Clinic_Persist_Grid_Settings');

  const [kickOutTarget, setKickOutTarget] = useState<KickOutTarget | null>(
    null,
  );
  const [reviewTarget, setReviewTarget] = useState<{
    recipId: string;
    tag: string;
    isClientRecip: boolean;
  } | null>(null);

  useEffect(() => {
    tagInputRef.current?.focus();
  }, []);

  const { data: recips = [] } = useGetRecipsQuery(yardId, { skip: !yardId });

  const visibleRecips = useMemo(
    () =>
      isRecipTagSearchEnabled && !clientRecipMode
        ? filterRecipsByTag(recips, recipTag)
        : recips,
    [isRecipTagSearchEnabled, clientRecipMode, recips, recipTag],
  );

  const { data: recipsInQueue = [] } = useGetRecipsInQueueQuery(
    { yardId, chute: chuteNumber },
    { skip: !yardId },
  );

  const [addRecipToQueue, { isLoading: isAdding }] =
    useAddRecipToQueueMutation();
  const [removeRecipFromQueue] = useRemoveRecipFromQueueMutation();
  const [clearRecipQueue, { isLoading: isClearing }] =
    useClearRecipQueueMutation();

  const handleAddRecip = useCallback(
    async (tag?: string) => {
      const tagToAdd = tag || recipTag.trim();
      if (!tagToAdd || !yardId) return;
      await addRecipToQueue({
        recipName: tagToAdd,
        yardId,
        chuteNumber,
        clientRecip: clientRecipMode,
      });
      setRecipTag('');
    },
    [recipTag, yardId, chuteNumber, clientRecipMode, addRecipToQueue],
  );

  const handleRemove = useCallback(
    (queueId: number) => {
      confirmDialog({
        message: 'Remove this recipient from the queue?',
        header: 'Confirm Remove',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Remove',
        rejectLabel: 'Cancel',
        accept: () => removeRecipFromQueue(queueId),
      });
    },
    [removeRecipFromQueue],
  );

  const handleClear = useCallback(() => {
    if (!yardId) return;
    confirmDialog({
      message: `Clear all ${recipsInQueue.length} recipients from the queue? This cannot be undone.`,
      header: 'Clear Queue',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Clear',
      rejectLabel: 'Cancel',
      accept: () => clearRecipQueue({ yardId, chute: chuteNumber }),
    });
  }, [yardId, chuteNumber, recipsInQueue.length, clearRecipQueue]);

  const handleSelect = useCallback(
    (item: RecipQueueItem) => {
      if (!selectedEmbryoId) return;
      onRecipSelect(getQueueDisplayName(item), item.queueId);
    },
    [selectedEmbryoId, onRecipSelect],
  );

  const recipColDefs = useMemo<ColDef<Recipient>[]>(
    () => [
      {
        field: 'tag',
        headerName: 'Tag',
        width: 90,
        cellRenderer: (params: ICellRendererParams<Recipient>) => {
          const data = params.data;
          if (!data?.tag) return null;
          if (isAnimalLinkEnabled) {
            return (
              <AnimalLink tagName={data.tag} openInNewTab stopPropagation />
            );
          }
          return data.tag;
        },
      },
      {
        field: 'recipStatusIdName',
        headerName: 'Status',
        width: 105,
        cellStyle: recipStatusCellStyle,
      },
      {
        field: 'estrusSync',
        headerName: 'ES #',
        width: 70,
        ...(isEstrusSyncEnabled && {
          valueFormatter: (params: ValueFormatterParams) =>
            params.value != null ? Number(params.value).toFixed(1) : '',
        }),
      },
      { field: 'kickOuts', headerName: 'KO', width: 60 },
      { field: 'lastDrugUsed', headerName: 'Drug', flex: 1, minWidth: 100 },
      {
        headerName: '',
        width: 90,
        cellRenderer: (params: ICellRendererParams<Recipient>) => {
          const data = params.data;
          if (!data?.tag) return null;
          return (
            <Button
              size="xs"
              variant="outline"
              onClick={() => handleAddRecip(data.tag!)}
            >
              +Queue
            </Button>
          );
        },
      },
    ],
    [handleAddRecip, isEstrusSyncEnabled, isAnimalLinkEnabled],
  );

  const queueColDefs = useMemo<ColDef<RecipQueueItem>[]>(
    () => [
      { field: 'chuteNumber', headerName: 'Chute #', width: 70 },
      {
        field: 'tag',
        headerName: 'Tag',
        width: 90,
        cellRenderer: (params: ICellRendererParams<RecipQueueItem>) => {
          const data = params.data;
          if (!data) return null;
          const isClientRecip = !!data.clientRecipTag;
          const displayName = data.clientRecipTag || data.tag;
          if (isAnimalLinkEnabled && !isClientRecip && data.tag) {
            return (
              <AnimalLink tagName={data.tag} openInNewTab stopPropagation />
            );
          }
          return displayName;
        },
      },
      {
        field: 'recipStatusIdName',
        headerName: 'Status',
        width: 105,
        cellStyle: recipStatusCellStyle,
      },
      {
        field: 'estrusSync',
        headerName: 'ES #',
        width: 70,
        ...(isEstrusSyncEnabled && {
          valueFormatter: (params: ValueFormatterParams) =>
            params.value != null ? Number(params.value).toFixed(1) : '',
        }),
      },
      { field: 'kickOuts', headerName: 'KO', width: 60 },
      {
        headerName: '',
        flex: 1,
        minWidth: 260,
        cellRenderer: (params: ICellRendererParams<RecipQueueItem>) => {
          const data = params.data;
          if (!data) return null;
          return (
            <div className="flex gap-1">
              <Button
                size="xs"
                disabled={!selectedEmbryoId}
                onClick={() => handleSelect(data)}
              >
                Select
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() =>
                  setKickOutTarget({
                    recipId: data.recipId,
                    recipQueueId: data.queueId,
                    recipStatus: data.recipStatusIdName,
                  })
                }
              >
                Kick Out
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() =>
                  setReviewTarget({
                    recipId: data.recipId,
                    tag: getQueueDisplayName(data),
                    isClientRecip: !!data.clientRecipTag,
                  })
                }
              >
                Review
              </Button>
              <Button
                size="xs"
                variant="destructive"
                onClick={() => handleRemove(data.queueId)}
              >
                Rm
              </Button>
            </div>
          );
        },
      },
    ],
    [
      selectedEmbryoId,
      handleSelect,
      handleRemove,
      isEstrusSyncEnabled,
      isAnimalLinkEnabled,
    ],
  );

  const queueGrid = (
    <DataGrid<RecipQueueItem>
      containerClassName={
        persistGridState
          ? PERSISTED_GRID_CONTAINER_CLASS
          : 'ag-theme-alpine ag-compact mt-1 h-[240px] overflow-hidden lg:h-auto lg:min-h-0 lg:flex-1'
      }
      rowData={recipsInQueue}
      columnDefs={queueColDefs}
      getRowId={(params) => `q_${params.data.queueId}`}
      overlayNoRowsTemplate="Queue is empty — add recipients from In Heat or by tag"
    />
  );

  const inHeatGrid = (
    <DataGrid<Recipient>
      containerClassName={
        persistGridState
          ? PERSISTED_GRID_CONTAINER_CLASS
          : 'ag-theme-alpine ag-compact mt-1 h-[280px] overflow-hidden lg:h-auto lg:min-h-0 lg:flex-1'
      }
      rowData={visibleRecips}
      columnDefs={recipColDefs}
      getRowId={(params) => `r_${params.data.id ?? params.data.recipId ?? ''}`}
      getContextMenuItems={getRecipContextMenuItems}
      overlayNoRowsTemplate={
        yardId
          ? 'No recipients in heat for this yard'
          : 'Select a yard to view recipients'
      }
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 lg:gap-3">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="flex flex-col lg:min-h-0 lg:flex-1">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-xs font-medium">
            Queue
            {clientRecipMode && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                Client mode
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">
              {recipsInQueue.length} queued
            </span>
            {recipsInQueue.length > 0 && (
              <Button
                size="xs"
                variant="outline"
                onClick={handleClear}
                disabled={isClearing}
              >
                {isClearing ? 'Clearing...' : 'Clear'}
              </Button>
            )}
          </div>
        </div>
        <div className="mt-1 flex gap-1">
          <Input
            ref={tagInputRef}
            placeholder={getRecipInputPlaceholder(clientRecipMode)}
            value={recipTag}
            onChange={(e) => setRecipTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddRecip()}
            className="h-7 min-w-0 flex-1 text-xs"
          />
          <Button
            size="xs"
            onClick={() => handleAddRecip()}
            disabled={isAdding || !yardId}
            title={getAddDisabledTitle(yardId, isAdding)}
          >
            Add
          </Button>
        </div>
        {persistGridState ? (
          <div className={QUEUE_GRID_WRAPPER_CLASS}>
            <WithLocalStorage storageKey="in-clinic-recip-queue-grid">
              {queueGrid}
            </WithLocalStorage>
          </div>
        ) : (
          queueGrid
        )}
      </div>

      <div className="flex flex-col border-t pt-3 lg:min-h-0 lg:flex-1">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium">In Heat</span>
          <span className="text-muted-foreground text-xs">
            {visibleRecips.length === recips.length
              ? `${recips.length} recips`
              : `${visibleRecips.length} of ${recips.length} recips`}
          </span>
        </div>
        {persistGridState ? (
          <div className={IN_HEAT_GRID_WRAPPER_CLASS}>
            <WithLocalStorage storageKey="in-clinic-recip-in-heat-grid">
              {inHeatGrid}
            </WithLocalStorage>
          </div>
        ) : (
          inHeatGrid
        )}
      </div>

      {kickOutTarget && (
        <HeatNotUsedDialog
          open={!!kickOutTarget}
          onOpenChange={(open) => {
            if (!open) setKickOutTarget(null);
          }}
          target={kickOutTarget}
          session={session}
          canWrite={!!yardId}
          onSuccess={() => {
            setKickOutTarget(null);
            toast.current?.show({
              severity: 'success',
              summary: 'Kick Out recorded',
              life: 2000,
            });
          }}
        />
      )}

      {reviewTarget && (
        <RecipHistoryDialog
          open={!!reviewTarget}
          onOpenChange={(open) => {
            if (!open) setReviewTarget(null);
          }}
          recipId={reviewTarget.recipId}
          recipTag={reviewTarget.tag}
          isClientRecip={reviewTarget.isClientRecip}
        />
      )}
    </div>
  );
}
